import { AlertTriangle, MapPin, Navigation, MapPinOff, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { GeoErrorStatus } from '@/services/timeAttendance/types';

interface GeofenceErrorDialogProps {
  open: boolean;
  onClose: () => void;
  onRetry: () => void;
  geoStatus?: GeoErrorStatus;
  message?: string;
  distance?: number;
  allowedRadius?: number;
  geofenceName?: string;
  geofenceAddress?: string;
}

export const GeofenceErrorDialog = ({
  open,
  onClose,
  onRetry,
  geoStatus = 'OUTSIDE_RADIUS',
  message,
  distance = 0,
  allowedRadius = 0,
  geofenceName,
  geofenceAddress,
}: GeofenceErrorDialogProps) => {
  // Defensive checks to prevent crashes
  const safeDistance = typeof distance === 'number' && !isNaN(distance) ? distance : 0;
  const safeAllowedRadius = typeof allowedRadius === 'number' && !isNaN(allowedRadius) ? allowedRadius : 0;
  const safeName = geofenceName || 'approved location';
  
  const distanceInKm = (safeDistance / 1000).toFixed(2);
  const allowedRadiusInKm = (safeAllowedRadius / 1000).toFixed(2);
  const excessDistance = Math.max(0, safeDistance - safeAllowedRadius);
  const excessDistanceInMeters = Math.round(excessDistance);

  const getDialogContent = () => {
    switch (geoStatus) {
      case 'NO_LOCATION':
        return {
          icon: <MapPinOff className="h-5 w-5 text-destructive" />,
          title: 'Location Not Available',
          description: message || 'We could not determine your location. Please enable location services and try again.',
          showRetry: true,
          instructions: [
            'Enable location services on your device',
            'Allow location access for this website in your browser settings',
            'Ensure GPS is enabled (if on mobile)',
            'Try again once location is enabled',
          ],
        };
      case 'NO_GEOFENCE_CONFIGURED':
        return {
          icon: <Settings className="h-5 w-5 text-destructive" />,
          title: 'Location Not Configured',
          description: message || 'No approved work locations have been configured for your company.',
          showRetry: false,
          instructions: [
            'Contact your administrator to set up work locations',
            'Work locations (geofences) must be configured before you can clock in/out',
            'Once configured, you can try again',
          ],
        };
      case 'OUTSIDE_RADIUS':
      default:
        return {
          icon: <AlertTriangle className="h-5 w-5 text-destructive" />,
          title: 'Outside Approved Location',
          description: 'You are currently outside the approved work location and cannot clock in/out.',
          showRetry: true,
          instructions: [
            'Move closer to the approved location',
            'Contact your manager if you believe this is an error',
            'Check your location permissions are enabled',
          ],
        };
    }
  };

  const content = getDialogContent();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
              {content.icon}
            </div>
            <DialogTitle>{content.title}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {content.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Location Info - Only show for OUTSIDE_RADIUS */}
          {geoStatus === 'OUTSIDE_RADIUS' && (
            <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium">{safeName}</p>
                  {geofenceAddress && (
                    <p className="text-xs text-muted-foreground">{geofenceAddress}</p>
                  )}
                </div>
              </div>

              {/* Distance Info */}
              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Your distance</span>
                  <Badge variant="destructive" className="gap-1">
                    <Navigation className="h-3 w-3" />
                    {safeDistance > 1000 ? `${distanceInKm} km` : `${Math.round(safeDistance)} m`}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Allowed radius</span>
                  <Badge variant="outline" className="gap-1">
                    {safeAllowedRadius > 1000 ? `${allowedRadiusInKm} km` : `${safeAllowedRadius} m`}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">You need to move closer by</span>
                  <Badge variant="secondary" className="font-semibold">
                    {excessDistanceInMeters} m
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="text-sm text-muted-foreground space-y-2">
            <p className="font-medium text-foreground">What you can do:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              {content.instructions.map((instruction, index) => (
                <li key={index}>{instruction}</li>
              ))}
            </ul>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            {content.showRetry ? 'Cancel' : 'Close'}
          </Button>
          {content.showRetry && (
            <Button onClick={onRetry} className="w-full sm:w-auto">
              Try Again
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
